// src/utils/api.js
/**
 * Returns the appropriate API URL for the given endpoint
 */
const getApiUrl = (endpoint) => {
  // Handle both absolute and relative paths
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  return path;
};

/**
 * Makes an API call with robust error handling
 * @param {string} endpoint - API endpoint to call
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response or mock response on error
 */
export async function callApi(endpoint, options = {}) {
  // Don't immediately skip API calls - try them first unless we've explicitly set local-only mode
  const forceLocalStorage = window.localStorage.getItem('forceLocalStorage') === 'true';
  
  if (forceLocalStorage || window.usingLocalStorageOnly) {
    console.log(`API call to ${endpoint} skipped (local-only mode)`);
    return createMockResponse(true, {
      message: 'Using local storage only',
      success: true,
      localOnly: true
    });
  }
  
  const url = getApiUrl(endpoint);
  console.log(`Calling API: ${url} with method: ${options.method || 'GET'}`);
  
  try {
    // Add default headers for all requests
    const fetchOptions = {
      ...options,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        ...options.headers,
      },
      // Add a timeout using AbortController for fetch
      signal: AbortSignal.timeout(10000) // 10 second timeout for API calls
    };
    
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      console.warn(`API response not OK: ${response.status} ${response.statusText}`);
      throw new Error(`API error: ${response.status} (${response.statusText})`);
    }
    
    // Check if we got HTML instead of JSON (error condition)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.warn('Received HTML instead of JSON from API');
      throw new Error('Received HTML response');
    }
    
    return response;
  } catch (error) {
    console.error(`API call to ${endpoint} failed:`, error);
    
    // Only set local-only mode if we need to due to network errors
    if (error.name === 'AbortError' || 
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network')) {
      console.log('Switching to local-only mode due to network error');
      window.usingLocalStorageOnly = true;
    }
    
    // Create a mock response object that has the same interface
    return createMockResponse(false, {
      error: true,
      message: `API call failed: ${error.message}`,
      clientFallback: true
    });
  }
}

/**
 * Creates a mock Response object for failed API calls
 */
function createMockResponse(ok, data) {
  const mockResponseBody = JSON.stringify(data);
  
  return {
    ok,
    status: ok ? 200 : 503,
    statusText: ok ? 'OK (Mock)' : 'Service Unavailable (Mock)',
    headers: new Headers({
      'Content-Type': 'application/json',
      'Mock-Response': 'true'
    }),
    json: async () => data,
    text: async () => mockResponseBody,
    clone: () => createMockResponse(ok, data)
  };
}

/**
 * Fetch with retry — retries on network failures and 5xx responses, but NOT
 * on 4xx (a client error like a bad payload won't succeed just by retrying).
 * Added after a real /api/submit-quiz 504 lost a completed quiz: Neon
 * (the Postgres host behind POSTGRES_URL_NO_SSL / DATABASE_URL_UNPOOLED /
 * POSTGRES_URL_NON_POOLING) suspends its compute after inactivity and can
 * take several seconds to wake on the next query — a single slow response
 * during that window shouldn't permanently lose the user's quiz data.
 * Returns the final Response as-is (even if !ok) so existing callers' own
 * `response.ok` / `.json()` handling doesn't need to change — only the
 * plain `fetch(...)` call needs swapping for `fetchWithRetry(...)`.
 */
export async function fetchWithRetry(url, options = {}, { retries = 2, retryDelayMs = 1500, timeoutMs = 8000 } = {}) {
  let lastResponse;
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    // Per-attempt timeout — this had none originally, so a request that
    // genuinely never resolves (e.g. the network stack isn't fully up yet
    // right after a cold app launch on a real device — confirmed live via
    // Xcode console: JS executes fine, onAuthStateChanged fires, then the
    // Dashboard's /api/user-stats call just never comes back, no error,
    // no retry, stuck on the loading spinner forever) had no way to ever
    // retry or fail — it just hung indefinitely. A plain 4xx/5xx at least
    // rejects/resolves; a stalled TCP connection does neither without this.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }
      lastResponse = response;
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs * (attempt + 1)));
    }
  }

  if (lastResponse) return lastResponse;
  throw lastError;
}

/**
 * Makes an API call with a fallback value if it fails
 * @param {string} endpoint - API endpoint to call
 * @param {Object} options - Fetch options
 * @param {any} fallbackData - Data to return if API call fails
 * @returns {Promise<any>} - JSON response or fallback data
 */
export async function callApiWithFallback(endpoint, options = {}, fallbackData = {}) {
  try {
    const response = await callApi(endpoint, options);
    return await response.json();
  } catch (error) {
    console.log(`Using fallback data for ${endpoint}`);
    return fallbackData;
  }
}